import type { KeyboardEvent, ReactNode } from 'react';

import { cn } from '../lib/cn';

export type TabItem<T extends string> = {
  value: T;
  label: ReactNode;
  /** Icône discrète à gauche du libellé. */
  icon?: ReactNode;
  /** Compteur ou pastille, à droite du libellé. */
  badge?: ReactNode;
};

export type TabsProps<T extends string> = {
  /** Préfixe des `id` générés, à réutiliser sur les `TabPanel` correspondants. */
  id: string;
  value: T;
  onChange: (value: T) => void;
  items: readonly TabItem<T>[];
  /**
   * `vertical` range les onglets en colonne, à gauche du contenu — la forme
   * qui convient aux écrans de réglages, où les sections sont un sommaire
   * qu'on garde sous les yeux.
   */
  orientation?: 'horizontal' | 'vertical';
  className?: string;
  /** Classes des onglets eux-mêmes (marge verticale, taille du texte…). */
  itemClassName?: string;
  'aria-label'?: string;
};

/**
 * Onglets de navigation dans une page.
 *
 * `tabIndex` mobile — seul l'onglet actif est atteignable au `Tab`, les autres
 * se parcourent aux flèches. C'est ce qu'attendent les lecteurs d'écran d'un
 * `tablist`, et ça évite d'imposer six tabulations pour traverser un en-tête.
 */
export function Tabs<T extends string>({
  id,
  value,
  onChange,
  items,
  orientation = 'horizontal',
  className,
  itemClassName,
  ...props
}: TabsProps<T>) {
  const vertical = orientation === 'vertical';
  const index = items.findIndex((item) => item.value === value);

  const surTouche = (event: KeyboardEvent<HTMLDivElement>) => {
    // Les flèches suivent la disposition : en colonne, « bas » va à l'onglet
    // suivant, et « droite » ne veut plus rien dire.
    const deplacements: Record<string, number> = vertical
      ? { ArrowDown: index + 1, ArrowUp: index - 1 }
      : { ArrowRight: index + 1, ArrowLeft: index - 1 };

    const suivant = { ...deplacements, Home: 0, End: items.length - 1 }[event.key];

    if (suivant === undefined) return;
    event.preventDefault();

    // Boucle aux extrémités : depuis le dernier onglet, « suivant » revient au
    // premier plutôt que de ne rien faire.
    const cible = items[(suivant + items.length) % items.length];
    if (cible) onChange(cible.value);
  };

  return (
    <div
      role="tablist"
      aria-orientation={orientation}
      onKeyDown={surTouche}
      className={cn(
        'flex',
        vertical
          ? // En colonne, la liste est un panneau à part entière, posé à côté
            // du contenu : sans cadre, les libellés flottaient contre le fond
            // de page sans qu'on voie où commence la navigation.
            'flex-col gap-1 rounded-card border border-border-subtle bg-surface p-2 shadow-card'
          : // Le trait de base court sous toute la rangée : c'est lui que
            // l'onglet actif interrompt de sa couleur. Un `gap` large plutôt
            // que le padding des boutons seul : à cinq onglets ou plus,
            // « Accès secondaires » et « Doctolib » collaient l'un à l'autre
            // sans rien pour dire où l'un finit et l'autre commence.
            'gap-6 overflow-x-auto border-b border-border-subtle',
        className,
      )}
      {...props}
    >
      {items.map((item) => {
        const actif = item.value === value;

        return (
          <button
            key={item.value}
            type="button"
            role="tab"
            id={`${id}-tab-${item.value}`}
            aria-selected={actif}
            aria-controls={`${id}-panel-${item.value}`}
            tabIndex={actif ? 0 : -1}
            onClick={() => onChange(item.value)}
            className={cn(
              'flex shrink-0 items-center gap-2 whitespace-nowrap text-sm font-medium transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600/30',
              // En colonne, le soulignement n'a plus de rangée où s'inscrire :
              // l'onglet actif se porte en aplat, comme un élément de menu.
              vertical
                ? cn(
                    'w-full rounded-field px-3 py-2.5 text-left',
                    actif
                      ? 'bg-brand-50 font-semibold text-brand-600'
                      : 'text-content-muted hover:bg-surface-muted hover:text-content',
                  )
                : cn(
                    // `px-1` et non `px-4` : la séparation vient désormais du
                    // `gap-6` du conteneur, un padding large en plus aurait
                    // doublé l'écart.
                    'border-b-2 px-1 py-3',
                    actif
                      ? 'border-brand-600 text-brand-600'
                      : 'border-transparent text-content-muted hover:border-border-subtle hover:text-content',
                  ),
              itemClassName,
            )}
          >
            {item.icon ? (
              <span aria-hidden="true" className="flex [&>svg]:size-5">
                {item.icon}
              </span>
            ) : null}
            {item.label}
            {item.badge !== undefined ? (
              <span
                className={cn(
                  'rounded-full px-2 py-0.5 text-xs font-semibold transition-colors',
                  // En colonne, la pastille se range au bord droit du menu,
                  // alignée avec celles des autres onglets.
                  vertical && 'ml-auto',
                  actif ? 'bg-brand-600/10 text-brand-600' : 'bg-surface-muted text-content-muted',
                )}
              >
                {item.badge}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

export type TabPanelProps = {
  /** Même `id` que le `Tabs` qui pilote ce panneau. */
  id: string;
  value: string;
  /** Onglet actuellement sélectionné. */
  active: string;
  children: ReactNode;
  className?: string;
};

/**
 * Contenu d'un onglet. Démonté quand l'onglet n'est pas actif : les panneaux
 * de cette application chargent leurs propres données, les garder montés
 * lancerait les requêtes des trois onglets à l'ouverture de la page.
 */
export function TabPanel({ id, value, active, children, className }: TabPanelProps) {
  if (value !== active) return null;

  return (
    <div
      role="tabpanel"
      id={`${id}-panel-${value}`}
      aria-labelledby={`${id}-tab-${value}`}
      tabIndex={0}
      className={cn('focus-visible:outline-none', className)}
    >
      {children}
    </div>
  );
}
